import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { teamApi, tasksApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Briefcase, MapPin, Award, BookOpen, Clock,
  CheckSquare, Edit, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const DRAGON_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/ot58j39v_Transparent%20BG%20Dragon.png';

const TeamMember = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchProfile = async () => {
    try {
      const response = await teamApi.getById(id);
      setProfile(response.data);
      setEditData({
        title: response.data.title || '',
        bio: response.data.bio || '',
        expertise_areas: response.data.expertise_areas?.join(', ') || '',
        certifications: response.data.certifications?.join(', ') || '',
        compliance_specialties: response.data.compliance_specialties?.join(', ') || '',
        jurisdictions: response.data.jurisdictions?.join(', ') || '',
        weekly_capacity_hours: response.data.weekly_capacity_hours || 40
      });
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const updatePayload = {
        user_id: id,
        title: editData.title,
        bio: editData.bio,
        expertise_areas: editData.expertise_areas.split(',').map(s => s.trim()).filter(Boolean),
        certifications: editData.certifications.split(',').map(s => s.trim()).filter(Boolean),
        compliance_specialties: editData.compliance_specialties.split(',').map(s => s.trim()).filter(Boolean),
        jurisdictions: editData.jurisdictions.split(',').map(s => s.trim()).filter(Boolean),
        weekly_capacity_hours: parseFloat(editData.weekly_capacity_hours) || 40
      };
      await teamApi.update(id, updatePayload);
      toast.success('Profile updated successfully!');
      setEditDialogOpen(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const getAvatarColor = (name) => {
    const colors = ['#106EFD', '#40CFDE', '#37C563', '#FF801D', '#E171EA', '#FFD166'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  };

  const getUtilizationColor = (utilization) => {
    if (utilization > 100) return 'bg-[#FF801D]';
    if (utilization > 80) return 'bg-[#FFD166]';
    return 'bg-[#37C563]';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'todo': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="empty-state py-16">
        <img src={DRAGON_MASCOT} alt="Dragon mascot" className="w-32 h-32 mb-4" />
        <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">Profile not found</h3>
        <Link to="/team">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Team
          </Button>
        </Link>
      </div>
    );
  }

  const utilization = (profile.current_allocated_hours / profile.weekly_capacity_hours) * 100;

  return (
    <div className="space-y-6" data-testid="team-member-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/team">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        
        <Avatar className="w-20 h-20">
          <AvatarImage src={profile.avatar_url} />
          <AvatarFallback 
            className="font-bold text-2xl text-white"
            style={{ backgroundColor: getAvatarColor(profile.user?.name) }}
          >
            {getInitials(profile.user?.name)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{profile.user?.name}</h1>
            <Badge variant="outline" className="capitalize">{profile.user?.role?.replace('_', ' ')}</Badge>
          </div>
          <p className="text-navy/60 mt-1">{profile.title || 'Team Member'}</p>
          {profile.user?.email && (
            <p className="text-sm text-navy/50 mt-1">{profile.user?.email}</p>
          )}
        </div>
        
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="edit-profile-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-['Amaranth']">Edit Profile</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateProfile} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  placeholder="e.g., Senior Compliance Consultant"
                />
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Input
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                  placeholder="Brief bio"
                />
              </div>
              <div className="space-y-2">
                <Label>Expertise Areas (comma-separated)</Label>
                <Input
                  value={editData.expertise_areas}
                  onChange={(e) => setEditData({ ...editData, expertise_areas: e.target.value })}
                  placeholder="BSA/AML, Consumer Lending"
                />
              </div>
              <div className="space-y-2">
                <Label>Certifications (comma-separated)</Label>
                <Input
                  value={editData.certifications}
                  onChange={(e) => setEditData({ ...editData, certifications: e.target.value })}
                  placeholder="CRCM, CAMS"
                />
              </div>
              <div className="space-y-2">
                <Label>Compliance Specialties (comma-separated)</Label>
                <Input
                  value={editData.compliance_specialties}
                  onChange={(e) => setEditData({ ...editData, compliance_specialties: e.target.value })}
                  placeholder="BSA/AML, TILA, ECOA"
                />
              </div>
              <div className="space-y-2">
                <Label>Jurisdictions (comma-separated)</Label>
                <Input
                  value={editData.jurisdictions}
                  onChange={(e) => setEditData({ ...editData, jurisdictions: e.target.value })}
                  placeholder="Federal, California, Texas"
                />
              </div>
              <div className="space-y-2">
                <Label>Weekly Capacity (hours)</Label>
                <Input
                  type="number"
                  value={editData.weekly_capacity_hours}
                  onChange={(e) => setEditData({ ...editData, weekly_capacity_hours: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="save-profile-btn">Save Changes</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60">Capacity</p>
                <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{Math.round(utilization)}%</p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${getUtilizationColor(utilization)}20` }}>
                <Clock className="w-6 h-6" style={{ color: getUtilizationColor(utilization).replace('bg-', '') }} />
              </div>
            </div>
            <div className="utilization-bar mt-2">
              <div className={`utilization-fill ${getUtilizationColor(utilization)}`} style={{ width: `${Math.min(utilization, 100)}%` }} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4">
            <p className="text-sm text-navy/60">Allocated Hours</p>
            <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{profile.current_allocated_hours || 0}h</p>
            <p className="text-xs text-navy/50">of {profile.weekly_capacity_hours || 40}h weekly</p>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4">
            <p className="text-sm text-navy/60">Active Tasks</p>
            <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">
              {profile.assigned_tasks?.filter(t => t.status !== 'done').length || 0}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4">
            <p className="text-sm text-navy/60">Completed Tasks</p>
            <p className="text-2xl font-bold text-[#37C563] font-['Amaranth']">
              {profile.assigned_tasks?.filter(t => t.status === 'done').length || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white border border-navy/10">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Assigned Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bio */}
            {profile.bio && (
              <Card className="bg-white border-navy/10">
                <CardHeader>
                  <CardTitle className="font-['Amaranth'] text-lg">About</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-navy/70">{profile.bio}</p>
                </CardContent>
              </Card>
            )}

            {/* Expertise */}
            <Card className="bg-white border-navy/10">
              <CardHeader>
                <CardTitle className="font-['Amaranth'] text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#106EFD]" />
                  Expertise Areas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.expertise_areas?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.expertise_areas.map((area, idx) => (
                      <Badge key={idx} variant="secondary">{area}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-navy/50 text-sm">No expertise areas listed</p>
                )}
              </CardContent>
            </Card>

            {/* Certifications */}
            <Card className="bg-white border-navy/10">
              <CardHeader>
                <CardTitle className="font-['Amaranth'] text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#FFD166]" />
                  Certifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.certifications?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.certifications.map((cert, idx) => (
                      <Badge key={idx} className="bg-[#FFD166]/20 text-[#1A2B5E]">{cert}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-navy/50 text-sm">No certifications listed</p>
                )}
              </CardContent>
            </Card>

            {/* Compliance Specialties */}
            <Card className="bg-white border-navy/10">
              <CardHeader>
                <CardTitle className="font-['Amaranth'] text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#E171EA]" />
                  Compliance Specialties
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.compliance_specialties?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.compliance_specialties.map((spec, idx) => (
                      <Badge key={idx} className="bg-[#E171EA]/20 text-[#1A2B5E]">{spec}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-navy/50 text-sm">No specialties listed</p>
                )}
              </CardContent>
            </Card>

            {/* Jurisdictions */}
            <Card className="bg-white border-navy/10 lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-['Amaranth'] text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#37C563]" />
                  Jurisdictions Covered
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.jurisdictions?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.jurisdictions.map((jur, idx) => (
                      <Badge key={idx} variant="outline">{jur}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-navy/50 text-sm">No jurisdictions listed</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <Card className="bg-white border-navy/10">
            <CardHeader>
              <CardTitle className="font-['Amaranth']">Assigned Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              {profile.assigned_tasks?.length === 0 ? (
                <div className="empty-state py-8">
                  <img src={DRAGON_MASCOT} alt="Dragon" className="w-20 h-20 mb-3" />
                  <p className="text-navy/60">No tasks assigned yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {profile.assigned_tasks?.map((task) => (
                    <div key={task.id} className="p-4 rounded-lg border border-navy/10 hover:border-navy/20 transition-all">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-[#1A2B5E]">{task.title}</h4>
                          {task.description && <p className="text-sm text-navy/60 mt-1">{task.description}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(task.status)}>{task.status?.replace('_', ' ')}</Badge>
                          <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-navy/50">
                        {task.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {task.estimated_hours > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {task.estimated_hours}h estimated
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamMember;
